import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PortalShell, { StatusPill } from "../components/PortalShell";
import NewVerificationDialog from "../components/NewVerificationDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api, fmtErr, inr, fileUrl } from "../api";
import { toast } from "sonner";
import {
  ShieldCheck, FileText, Building2, IndianRupee, LifeBuoy, Plus, MapPin, ArrowRight, Loader2, Clock4,
} from "lucide-react";

export default function IndividualDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("overview");
  const [verifications, setVerifications] = useState([]);
  const [payments, setPayments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [applications, setApplications] = useState([]);
  const [city, setCity] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [support, setSupport] = useState({ subject: "", message: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, p, props, apps] = await Promise.all([
        api.get("/verifications"), api.get("/payments"),
        api.get("/properties", { params: city ? { city } : {} }),
        api.get("/properties/my-applications"),
      ]);
      setVerifications(v.data);
      setPayments(p.data);
      setProperties(props.data);
      setApplications(apps.data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  }, [city]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setShowNew(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const active = verifications.filter((v) => !["report_ready"].includes(v.status));
  const ready = verifications.filter((v) => v.status === "report_ready");
  const awaitingPayment = verifications.filter((v) => v.status === "report_locked");

  const apply = async (unitId) => {
    try {
      await api.post("/properties/apply", { unit_id: unitId, message: "" });
      toast.success("Application submitted to the builder.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const sendSupport = async (e) => {
    e.preventDefault();
    try {
      await api.post("/support", support);
      toast.success("Support request sent. We'll reach out shortly.");
      setSupport({ subject: "", message: "" });
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <PortalShell title="My Portal" subtitle="Verification requests, properties, payments and support"
      actions={<Button data-testid="new-verification-button" onClick={() => setShowNew(true)}><Plus className="mr-1.5 h-4 w-4" /> New Verification</Button>}>
      <NewVerificationDialog open={showNew} onOpenChange={setShowNew} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="individual-tabs" className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="verifications" data-testid="tab-verifications">My Verifications</TabsTrigger>
          <TabsTrigger value="properties" data-testid="tab-properties">Property Directory</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="support" data-testid="tab-support">Support</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6" data-testid="overview-panel">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              { label: "Active Requests", value: active.length, icon: Clock4, tid: "stat-active" },
              { label: "Reports Ready", value: ready.length, icon: FileText, tid: "stat-ready" },
              { label: "Awaiting Payment", value: awaitingPayment.length, icon: IndianRupee, tid: "stat-payment" },
              { label: "My Applications", value: applications.length, icon: Building2, tid: "stat-applications" },
            ].map((s) => (
              <div key={s.label} className="card-lift rounded-xl border border-slate-200 bg-white p-5" data-testid={s.tid}>
                <s.icon className="h-5 w-5 text-blue-600" />
                <p className="mt-3 text-3xl font-extrabold text-slate-900">{s.value}</p>
                <p className="mt-1 text-xs font-medium text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
          <h3 className="mt-10 text-lg font-semibold text-slate-900">Recent verification requests</h3>
          <div className="mt-4 space-y-3" data-testid="overview-verification-list">
            {loading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
            {!loading && verifications.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
                <ShieldCheck className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">No verification requests yet. Upload your Sale Deed, Khata and EC to get your first AI report.</p>
                <Button className="mt-4" data-testid="empty-new-verification-button" onClick={() => setShowNew(true)}>Start Verification</Button>
              </div>
            )}
            {verifications.slice(0, 4).map((v) => (
              <button key={v.id} onClick={() => navigate(`/verification/${v.id}`)} data-testid={`verification-row-${v.id}`}
                className="flex w-full items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 text-left transition-shadow hover:shadow-md">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">{v.property.address}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {new Date(v.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    {v.report_no ? ` · ${v.report_no}` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <StatusPill status={v.status} label={v.status_label} />
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </div>
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="verifications" className="mt-6" data-testid="verifications-panel">
          <div className="space-y-3">
            {verifications.map((v) => (
              <button key={v.id} onClick={() => navigate(`/verification/${v.id}`)} data-testid={`verification-list-row-${v.id}`}
                className="flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-5 text-left transition-shadow hover:shadow-md">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{v.property.address}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Survey: {v.property.survey_number || "—"} · {v.property.district || "—"}
                    {v.risk ? ` · Risk: ${v.risk.level}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusPill status={v.status} label={v.status_label} />
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </div>
              </button>
            ))}
            {!loading && verifications.length === 0 && <p className="text-sm text-slate-500">No requests yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="properties" className="mt-6" data-testid="properties-panel">
          <div className="mb-6 flex gap-2">
            <Input placeholder="Filter by city — Bengaluru, Mysuru…" value={city} onChange={(e) => setCity(e.target.value)}
              className="max-w-xs" data-testid="property-city-filter" />
            <Button variant="outline" onClick={load} data-testid="property-filter-apply">Apply</Button>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {properties.map((p) => (
              <div key={p.id} className="card-lift overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`directory-project-${p.id}`}>
                <div className="flex gap-4 p-5">
                  <img src={fileUrl(p.image_url)} alt={p.name} className="h-24 w-32 rounded-lg object-cover" />
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-slate-900">{p.name}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3 w-3" />{p.location}, {p.city}</p>
                    <p className="mt-0.5 font-mono text-[10px] text-slate-400">RERA: {p.rera_id}</p>
                    <p className="mt-1 text-xs text-slate-600">by {p.builder_name}</p>
                  </div>
                </div>
                <div className="border-t border-slate-100 px-5 py-3">
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">Available units</p>
                  <div className="space-y-2">
                    {p.units.slice(0, 3).map((u) => {
                      const applied = applications.some((a) => a.unit_id === u.id);
                      return (
                        <div key={u.id} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
                          <span className="text-sm text-slate-700">Unit {u.unit_no} · {u.unit_type} · {u.area_sqft} sq.ft</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-slate-900">{inr(u.price)}</span>
                            {applied
                              ? <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700" data-testid={`applied-badge-${u.id}`}>Applied</span>
                              : <Button size="sm" variant="outline" data-testid={`apply-unit-${u.id}`} onClick={() => apply(u.id)}>Apply</Button>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
            {!loading && properties.length === 0 && <p className="text-sm text-slate-500">No published properties right now.</p>}
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-6" data-testid="payments-panel">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-5 py-3">Order</th><th className="px-5 py-3">Amount</th><th className="px-5 py-3">Mode</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Date</th></tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`payment-row-${p.id}`}>
                    <td className="px-5 py-3 font-mono text-xs">{p.order_id}</td>
                    <td className="px-5 py-3 font-semibold">{p.mode === "live" ? inr(p.amount / 100) : "—"}</td>
                    <td className="px-5 py-3"><span className="rounded bg-amber-50 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-700">{p.mode}</span></td>
                    <td className="px-5 py-3 capitalize">{p.status}</td>
                    <td className="px-5 py-3 text-slate-500">{new Date(p.created_at).toLocaleDateString("en-IN")}</td>
                  </tr>
                ))}
                {payments.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">No payments yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="support" className="mt-6" data-testid="support-panel">
          <form onSubmit={sendSupport} className="max-w-lg space-y-4 rounded-xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 text-slate-900"><LifeBuoy className="h-5 w-5 text-blue-600" /><p className="font-semibold">Raise a support request</p></div>
            <div className="space-y-1.5">
              <Label htmlFor="s-subject">Subject</Label>
              <Input id="s-subject" required data-testid="support-subject-input" value={support.subject} onChange={(e) => setSupport((s) => ({ ...s, subject: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-message">Message</Label>
              <Textarea id="s-message" required rows={4} data-testid="support-message-input" value={support.message} onChange={(e) => setSupport((s) => ({ ...s, message: e.target.value }))} />
            </div>
            <Button type="submit" data-testid="support-submit-button">Send Request</Button>
          </form>
        </TabsContent>
      </Tabs>
    </PortalShell>
  );
}
